import React from 'react';

/**
 * How many frames either side of the playhead may pre-fetch once the map has
 * settled. Warming the neighbours is what keeps scrubbing and playback from
 * flashing; it just must not happen *while* the user is moving the map.
 */
export const RADAR_NEIGHBOUR_RANGE = 2;

/**
 * How long a move may hold the gate shut without an explicit settle signal.
 *
 * The settle signal can genuinely go missing. On iOS `animateToRegion` sets
 * `ignoreRegionChanges`, which suppresses the region events entirely; on the
 * web `idle` waits on the *whole* map, so a basemap or station source that
 * never finishes would never release the gate. Either would strand the radar
 * on the current frame alone and make every scrub and playback tick cold —
 * a worse bug than the one this hook exists to fix. So the gate always
 * re-opens on its own, and the settle signals are only an optimisation.
 */
export const RADAR_SETTLE_TIMEOUT_MS = 4000;

/**
 * How long after the frame list first arrives the settle signal is ignored.
 *
 * The visible frame is almost always the newest one, whose tiles no CDN edge
 * has seen yet — every one is an on-demand render at the origin. Measured on
 * device, the map fires its settle signal well under a second after the
 * overlays mount, and honouring it put the neighbour frames' equally-cold
 * tiles on the wire alongside the visible frame's: 24 simultaneous origin
 * renders instead of 8, all slower for it. So for this brief window the
 * current frame keeps the origin to itself; the watchdog still re-opens the
 * window on its own, so an ignored settle can never strand the gate.
 */
export const RADAR_FIRST_PAINT_GRACE_MS = 2500;

type Options = {
  timestamps: number[];
  currentTimestamp: number | undefined;
  neighbourRange?: number;
};

export type RadarFetchPhase = {
  /** The frames allowed to hit the network right now. */
  allowedTimestamps: Set<number>;
  /** True while the gate is narrowed to the visible frame. */
  currentOnly: boolean;
  /** Call from every map move/zoom event (fine to call at gesture rate). */
  onInteractionStart: () => void;
  /** Call once the map has stopped and the visible frame has painted. */
  onSettled: () => void;
};

/**
 * Decides which radar frames may fetch, so the frame on screen never queues
 * behind invisible ones.
 *
 * Every frame stays mounted (that is what makes scrubbing flash-free), so
 * "may fetch" is the only lever on request volume — and both platforms funnel
 * every tile through one fixed-size, priority-free queue: MapLibre's global
 * 16-slot FIFO image queue on the web, a `Semaphore(12)` / 12-connection pool
 * on Android and iOS. With the static current ±2 window, a pan or zoom put
 * five frames' worth of tiles into that queue and the visible frame landed
 * roughly fifth. Worse on the web, where sources are iterated in insertion
 * (ascending-timestamp) order: with the playhead at the live edge the window
 * wraps to the two *oldest* frames, which enqueue their whole viewport first.
 *
 * So the window is not static any more:
 *
 * - moving  → the current frame and nothing else
 * - settled → current ±`neighbourRange`, wrapping circularly
 *
 * It starts narrowed, before any gesture, and re-narrows when the frame list
 * first arrives: the *first* paint has the same problem as a pan, because all
 * five frames become visible the moment the metadata resolves and the one on
 * screen is again fifth in the queue.
 *
 * Gated on map movement only, deliberately — not on the playhead moving. The
 * 750 ms playback tick changes `currentTimestamp` constantly, and treating
 * that as an interaction would hold the gate shut for the whole animation and
 * leave every frame cold.
 */
const useRadarFetchPhase = ({
  timestamps,
  currentTimestamp,
  neighbourRange = RADAR_NEIGHBOUR_RANGE,
}: Options): RadarFetchPhase => {
  const [currentOnly, setCurrentOnly] = React.useState(true);
  const watchdogRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  const clearWatchdog = React.useCallback(() => {
    if (watchdogRef.current !== undefined) {
      clearTimeout(watchdogRef.current);
      watchdogRef.current = undefined;
    }
  }, []);

  const armWatchdog = React.useCallback(() => {
    clearWatchdog();
    watchdogRef.current = setTimeout(() => {
      watchdogRef.current = undefined;
      setCurrentOnly(false);
    }, RADAR_SETTLE_TIMEOUT_MS);
  }, [clearWatchdog]);

  const graceUntilRef = React.useRef(0);

  const onSettled = React.useCallback(() => {
    // First paint in progress — see RADAR_FIRST_PAINT_GRACE_MS. The watchdog
    // stays armed and opens the window on its own.
    if (Date.now() < graceUntilRef.current) {
      return;
    }
    clearWatchdog();
    setCurrentOnly(false);
  }, [clearWatchdog]);

  // Mobile streams region events for the whole gesture, so this re-arms rather
  // than starting a single timer: the watchdog must not fire under a slow pan.
  // Identity is stable (refs only) because the mobile screen hands it straight
  // to onRegionChange, which fires many times a second.
  const onInteractionStart = React.useCallback(() => {
    armWatchdog();
    setCurrentOnly(true);
  }, [armWatchdog]);

  // Narrow again the moment the frame list first arrives.
  //
  // Until it does, `allowedTimestamps` is empty and nothing is fetching, so the
  // initial narrow state is a no-op — the instant it stops being a no-op is
  // exactly when the frames appear. And a settle can easily land before then:
  // on the web `idle` fires as soon as the *basemap* is loaded, which races the
  // separate weather-maps.json request. Without this, that early idle opens the
  // gate and all five frames light up together a moment later — the very
  // pile-up this hook exists to prevent, just moved to page load.
  const hasFrames = timestamps.length > 0 && currentTimestamp != null;
  const hadFramesRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasFrames || hadFramesRef.current) {
      return;
    }
    hadFramesRef.current = true;
    graceUntilRef.current = Date.now() + RADAR_FIRST_PAINT_GRACE_MS;
    armWatchdog();
    setCurrentOnly(true);
  }, [hasFrames, armWatchdog]);

  // Arm the watchdog at mount as well: the gate starts narrow, so a client that
  // never sends a settle signal must not be stuck on one frame forever.
  React.useEffect(() => {
    armWatchdog();
    return clearWatchdog;
  }, [armWatchdog, clearWatchdog]);

  const allowedTimestamps = React.useMemo(() => {
    const allowed = new Set<number>();
    if (currentTimestamp == null || timestamps.length === 0) {
      return allowed;
    }
    const idx = timestamps.indexOf(currentTimestamp);
    // Not served any more — a refresh dropped the frame the user was on. The
    // caller re-points the playhead next render; allow nothing until it does.
    if (idx === -1) {
      return allowed;
    }
    allowed.add(currentTimestamp);
    if (currentOnly) {
      return allowed;
    }
    const len = timestamps.length;
    for (let i = -neighbourRange; i <= neighbourRange; i++) {
      allowed.add(timestamps[(((idx + i) % len) + len) % len]);
    }
    return allowed;
  }, [timestamps, currentTimestamp, currentOnly, neighbourRange]);

  return React.useMemo(
    () => ({
      allowedTimestamps,
      currentOnly,
      onInteractionStart,
      onSettled,
    }),
    [allowedTimestamps, currentOnly, onInteractionStart, onSettled],
  );
};

export default useRadarFetchPhase;
